import React, { type HTMLAttributes } from 'react';
import { cn } from '../theme';

// ============================================================================
// SKELETON
// ============================================================================

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * If true, uses a circular shape instead of rectangular.
   */
  circle?: boolean;
}

/**
 * Skeleton loading placeholder component.
 * 
 * @example
 * // Basic skeleton
 * <Skeleton className="h-4 w-full" />
 * 
 * // Circle (for avatars)
 * <Skeleton circle className="h-10 w-10" />
 * 
 * // Card skeleton
 * <div className="space-y-2">
 *   <Skeleton className="h-32 w-full" />
 *   <Skeleton className="h-4 w-3/4" />
 *   <Skeleton className="h-4 w-1/2" />
 * </div>
 */
function Skeleton({ className, circle = false, ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse bg-cream-dark',
        circle ? 'rounded-full' : 'rounded-md',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// SKELETON TEXT
// ============================================================================

interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Number of lines to render.
   */
  lines?: number;
}

/**
 * Skeleton for text content with multiple lines.
 */
function SkeletonText({ className, lines = 3, ...props }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)} {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 ? 'w-2/3' : 'w-full'
          )}
        />
      ))}
    </div>
  );
}

// ============================================================================
// SKELETON CARD
// ============================================================================

interface SkeletonCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to show an image placeholder.
   */
  hasImage?: boolean;
}

/**
 * Pre-built skeleton for card layouts.
 */
function SkeletonCard({ className, hasImage = true, ...props }: SkeletonCardProps) {
  return (
    <div
      className={cn('rounded-lg border border-warm-gray/10 p-4 space-y-4', className)}
      {...props}
    >
      {hasImage && <Skeleton className="h-40 w-full" />}
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

// ============================================================================
// SKELETON AVATAR
// ============================================================================

interface SkeletonAvatarProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
}

function SkeletonAvatar({ className, size = 'md', ...props }: SkeletonAvatarProps) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  return (
    <Skeleton
      circle
      className={cn(sizeClasses[size], className)}
      {...props}
    />
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { Skeleton, SkeletonText, SkeletonCard, SkeletonAvatar };
